import React, { useEffect, useRef } from 'react';

/**
 * <Tilt> — premium 3D mouse-tilt wrapper.
 *
 * The child element rotates subtly on rotateX / rotateY based on cursor
 * position inside the wrapper. Pure GPU (transform-only), rAF-batched.
 * Skipped on coarse-pointer devices and when `prefers-reduced-motion`.
 *
 * On coarse-pointer / reduced-motion the wrapper renders as a PLAIN
 * `<div>` — no `transform-style: preserve-3d`, no `will-change`, and no
 * `mix-blend-mode: screen` glare layer. This matters on iOS Safari and
 * older Android browsers where `mix-blend-mode` forces every Tilt
 * subtree onto its own GPU layer, multiplying memory cost across
 * sections (Trainers' 4 cards alone were costing ~4 extra layers).
 *
 * Props:
 *   max         — peak tilt in degrees on each axis (default 8)
 *   scale       — hover scale (default 1.015)
 *   perspective — perspective in px (default 1000)
 *   glare       — show a subtle highlight that tracks the cursor (default true)
 */
export default function Tilt({
  children,
  max = 8,
  scale = 1.015,
  perspective = 1000,
  glare = true,
  className = '',
  ...rest
}) {
  const ref = useRef(null);
  const glareRef = useRef(null);

  // Snapshot once at mount: are we on a touch / older / reduced-motion
  // device? If so render the lightweight passthrough below. We also
  // gate on `(hover: none)` so iPad Safari (which can report
  // pointer: fine with an attached trackpad) is still treated as a
  // touch device for animation/effect purposes.
  const isLight = typeof window !== 'undefined' && window.matchMedia
    && (window.matchMedia('(pointer: coarse)').matches
      || window.matchMedia('(hover: none)').matches
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(hover: none)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const el = ref.current;
    if (!el) return undefined;

    let pending = false;
    let lastE = null;
    let active = false;

    const apply = () => {
      pending = false;
      if (!lastE || !active) return;
      const rect = el.getBoundingClientRect();
      const px = (lastE.clientX - rect.left) / rect.width; // 0..1
      const py = (lastE.clientY - rect.top) / rect.height;
      const rx = (0.5 - py) * 2 * max; // top → +max
      const ry = (px - 0.5) * 2 * max; // right → +max
      el.style.transform =
        `perspective(${perspective}px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(${scale})`;
      if (glareRef.current) {
        glareRef.current.style.opacity = '1';
        glareRef.current.style.background =
          `radial-gradient(420px circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%, rgba(255,255,255,0.18), rgba(255,82,82,0.08) 35%, transparent 60%)`;
      }
    };

    const onMove = (e) => {
      lastE = e;
      if (!pending) {
        pending = true;
        requestAnimationFrame(apply);
      }
    };
    const onEnter = () => {
      active = true;
      el.style.transition = 'transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)';
    };
    const onLeave = () => {
      active = false;
      el.style.transition = 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)';
      el.style.transform = `perspective(${perspective}px) rotateX(0deg) rotateY(0deg) scale(1)`;
      if (glareRef.current) glareRef.current.style.opacity = '0';
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('mousemove', onMove);
    };
  }, [max, scale, perspective]);

  // ----- LIGHTWEIGHT MOBILE / TOUCH PATH -----
  // No preserve-3d, no will-change, no glare, no mix-blend-mode.
  if (isLight) {
    return (
      <div className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`tilt-3d ${className}`}
      style={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
      {...rest}
    >
      {children}
      {glare && (
        <div
          ref={glareRef}
          aria-hidden
          className="tilt-glare"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.35s ease',
            borderRadius: 'inherit',
            mixBlendMode: 'screen',
            zIndex: 5,
          }}
        />
      )}
    </div>
  );
}
